import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * One approval table widget a classification job in this domain could deliver rows from.
 *
 * Already flattened by the backend — an `(app, widget)` pair rather than apps carrying widgets —
 * because a job names exactly one pair. A nested shape would only be flattened again here to fill
 * the single dropdown that consumes it.
 */
export type ApprovalWidget = {
  app_id: number;
  app_name: string;
  table_id: number;
  label: string;
};

export type JobApprovalWidgetsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  /**
   * Keyed by domain, because that is what the endpoint is scoped to. Two classification jobs in
   * one domain choose from the same list, so opening the second job's card after the first costs
   * no request.
   */
  widgetsByDomainId: Record<number, ApprovalWidget[]>;
};

export const jobApprovalWidgetsInitialState: JobApprovalWidgetsState = {
  status: STATUS.IDLE,
  error: null,
  widgetsByDomainId: {},
};

function isApprovalWidget(value: unknown): value is ApprovalWidget {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const widget = value as Record<string, unknown>;
  return (
    typeof widget.app_id === "number" &&
    typeof widget.app_name === "string" &&
    typeof widget.table_id === "number" &&
    typeof widget.label === "string"
  );
}

export const fetchJobApprovalWidgets = createAsyncThunk<
  { domainId: number; widgets: ApprovalWidget[] },
  number,
  { rejectValue: string }
>("jobApprovalWidgets/fetch", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/domains/${domainId}/approval-widgets`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isApprovalWidget)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, widgets: data };
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

const jobApprovalWidgetsSlice = createSlice({
  name: "jobApprovalWidgets",
  initialState: jobApprovalWidgetsInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobApprovalWidgets.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchJobApprovalWidgets.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.widgetsByDomainId[action.payload.domainId] = action.payload.widgets;
        state.error = null;
      })
      .addCase(fetchJobApprovalWidgets.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      });
  },
});

export const selectApprovalWidgets = (state: RootState, domainId: number): ApprovalWidget[] =>
  state.jobApprovalWidgets.widgetsByDomainId[domainId] ?? [];

export const selectApprovalWidgetsStatus = (state: RootState) => state.jobApprovalWidgets.status;

export const jobApprovalWidgetsReducer = jobApprovalWidgetsSlice.reducer;
